/**
 * A start/stop timer that survives the app being closed.
 *
 * Only the start timestamp is persisted — never a running counter — so elapsed
 * time is recomputed from the clock on every load. Closing the tab, locking the
 * phone or rebooting therefore cannot lose or distort a running session.
 */

import { msToHours } from '$shared/dates';
import * as db from './db.svelte';

const STORAGE_KEY = 'timesheet:timer';

interface RunningTimer {
  clientId: string;
  startedAt: number;
  note?: string;
}

class Timer {
  running = $state<RunningTimer | null>(null);
  /** Ticks once a second while running, purely to drive the display. */
  now = $state(Date.now());

  private interval: ReturnType<typeof setInterval> | null = null;

  elapsedMs = $derived(this.running ? Math.max(0, this.now - this.running.startedAt) : 0);

  /** Decimal hours at 0.5 granularity — what actually gets logged. */
  roundedHours = $derived(msToHours(this.elapsedMs, 0.5));

  display = $derived.by(() => {
    const total = Math.floor(this.elapsedMs / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;
  });

  async init(): Promise<void> {
    // localStorage is synchronous, so the timer paints on the very first frame;
    // IndexedDB is the durable copy in case localStorage is cleared.
    const local = readLocal();
    this.running = local ?? (await db.getMeta<RunningTimer>('timer')) ?? null;
    if (this.running) this.startTicking();
  }

  start(clientId: string, note?: string): void {
    this.running = { clientId, startedAt: Date.now(), note };
    this.persist();
    this.startTicking();
  }

  /** Stop and return the rounded hours, for the caller to put in the form. */
  stop(): { clientId: string; hours: number; note?: string } | null {
    if (!this.running) return null;
    const result = {
      clientId: this.running.clientId,
      hours: this.roundedHours,
      note: this.running.note,
    };
    this.cancel();
    return result;
  }

  cancel(): void {
    this.running = null;
    this.stopTicking();
    localStorage.removeItem(STORAGE_KEY);
    void db.setMeta('timer', null);
  }

  private persist(): void {
    if (!this.running) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.running));
    void db.setMeta('timer', this.running);
  }

  private startTicking(): void {
    this.now = Date.now();
    this.stopTicking();
    this.interval = setInterval(() => (this.now = Date.now()), 1000);
  }

  private stopTicking(): void {
    if (this.interval) clearInterval(this.interval);
    this.interval = null;
  }
}

function readLocal(): RunningTimer | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RunningTimer;
    return typeof parsed?.startedAt === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

export const timer = new Timer();
